import i18n from 'src/lib/i18n/i18n';
import { PaymentResult, StripePaymentMethod } from '../../iap.types';
import { useEffect, useState } from 'preact/hooks';
import {
  LOCAL_CURRENCY,
  ProductId,
  webIAPPrices,
} from 'src/replicant/iap/iap.ruleset';
import { getStripe, stripeCreatePayment } from '../../providers/stripe';
import { getReturnUrl } from 'src/lib/iap/iap.util';

/**
 * Handle Stripe payments. Show required fields and forms according to the payment method.
 * @param
 * @returns
 */
export function StripeHandler({
  productId,
  paymentMethod,
  close,
  onPurchaseStart,
}: {
  productId: ProductId;
  paymentMethod: StripePaymentMethod;
  close: (result: PaymentResult) => void;
  onPurchaseStart: () => void;
}) {
  // Message.
  const [message, setMessage] = useState<string>('');

  // On mount.
  useEffect(() => {
    // Set loading message.
    setMessage(
      `<div class="lds-ring"><div></div><div></div><div></div><div></div></div>`,
    );

    // Load stripe sdk.
    getStripe().then((stripe) => {
      // Set up Stripe.js and Elements to use in checkout form
      const elements = stripe.elements({
        mode: 'payment',
        amount: webIAPPrices[productId],
        currency: LOCAL_CURRENCY.toLocaleLowerCase(),
        loader: 'always',
      });

      // Create and mount the Express Checkout Element
      const expressCheckoutElement = elements.create('expressCheckout', {
        wallets: {
          googlePay: paymentMethod === 'stripe_googlePay' ? 'always' : 'never',
          applePay: paymentMethod === 'stripe_applePay' ? 'always' : 'never',
        },
      });
      expressCheckoutElement.mount('#express-checkout');

      expressCheckoutElement.on('ready', (event) => {
        if (!event?.availablePaymentMethods) {
          setMessage(i18n('openWebPay.notSupported'));
        } else {
          setMessage(''); // Remove loading message
        }
      });

      expressCheckoutElement.on('loaderror', (event) => {
        setMessage(`Loading error: ${event.error.message ?? 'Unknown'}`);
      });

      expressCheckoutElement.on('click', async (event) => {
        onPurchaseStart();

        event.resolve();
      });

      expressCheckoutElement.on('confirm', async () => {
        const handleError = (error: Error) => {
          setMessage(error.message);
        };

        const { error: submitError } = await elements.submit();
        if (submitError) {
          handleError(new Error(submitError.message));
          return;
        }

        // Create payment intent
        const { clientSecret, purchaseID } =
          await stripeCreatePayment(productId);

        // Handle result.error or result.paymentIntent.
        const { error, paymentIntent } = await stripe!.confirmPayment({
          // `elements` instance used to create the Express Checkout Element
          elements,
          // `clientSecret` from the created PaymentIntent
          clientSecret,
          confirmParams: {
            return_url: getReturnUrl(),
          },
          redirect: 'if_required',
        });

        if (error) {
          // This point is only reached if there's an immediate error when
          // confirming the payment. Show the error to your customer (for example, payment details incomplete)
          handleError(new Error(error.message));
        } else {
          // The payment UI automatically closes with a success animation.
          // Your customer is redirected to your `return_url`.
          close({
            success: true,
            provider: 'stripe',
            paymentMethod,
            internalTransactionId: paymentIntent.id,
            purchaseID: purchaseID,
          });
        }
      });
    });
  }, []);

  // Return the Stripe fields container. And add extra information/requirements.
  return (
    <>
      <div class="paymentForm" dangerouslySetInnerHTML={{ __html: message }} />
      <div id="express-checkout" />
    </>
  );
}
